/**
 * Live mode's first-run flags, per browser (docs/plans/os-voice-conversation.md,
 * step 3.8): what this browser has already seen once.
 *
 * - `muteTipSeen`: the "Tap to mute" tip over Mute was shown (3.12).
 * - `talkLiveWordSeen`: the words beside the composer's orb were shown (3.9).
 * - `halfDuplexNoteSeen`: the note beside Interrupt ("Voice can't interrupt
 *   while OS talks. Earphones fix this.") was shown once (3.12).
 * - `privacySeen`: the studio's "What I hear in live mode" note was
 *   dismissed with Got it (step 7.5 of docs/plans/onboarding-harness/phase-07.md).
 * - `helloSeen`: Kivi said hello on setup's first open in this browser
 *   (docs/plans/kivi-orb.md, Decided 12): it does once per device.
 *
 * Each read and write is in try/catch: a storage that throws (a private
 * window, a blocked site) or comes back empty only means "the first time
 * again". Nothing here is kept anywhere but this browser.
 */

export type LiveFirstRunFlag = 'muteTipSeen' | 'talkLiveWordSeen' | 'halfDuplexNoteSeen' | 'privacySeen' | 'helloSeen';

export const LIVE_FIRST_RUN_FLAGS: readonly LiveFirstRunFlag[] = ['muteTipSeen', 'talkLiveWordSeen', 'halfDuplexNoteSeen', 'privacySeen', 'helloSeen'];

/** The storage key a flag is kept under. */
export const firstRunKey = (flag: LiveFirstRunFlag): string => `alluzo.live.${flag}`;

/** What the flags read and write through: `localStorage`, or a stand-in in a test. */
export type FirstRunStorage = Pick<Storage, 'getItem' | 'setItem'>;

const storageNow = (): FirstRunStorage | null => {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
};

/** Whether this browser has the flag. A storage that throws or holds nothing says no. */
export const hasSeen = (flag: LiveFirstRunFlag, storage: FirstRunStorage | null = storageNow()): boolean => {
  if (storage === null) return false;
  try {
    return storage.getItem(firstRunKey(flag)) === '1';
  } catch {
    return false;
  }
};

/** Keep the flag in this browser. A storage that throws keeps nothing, and that is all. */
export const markSeen = (flag: LiveFirstRunFlag, storage: FirstRunStorage | null = storageNow()): void => {
  if (storage === null) return;
  try {
    storage.setItem(firstRunKey(flag), '1');
  } catch {
    /* The first time again, next time. */
  }
};
